import fp from 'lodash/fp';
import React, { FC, useCallback, useMemo } from 'react';
import { Form as FinalForm, Field, FormSpy, FormProps as FinalFormProps } from 'react-final-form';
import { checkSnils, checkOgrn, checkOgrnip } from 'ru-validation-codes';
import { DaDataSuggestion, DaDataParty } from 'react-dadata';
import { Common } from '@factorin/domain';

import { Button, GridColumn, GridRow, Label, Text, linkAnchorClass } from 'Modules/ui2';
import { getError, validators } from 'Modules/core/forms';

import { CertificateField, InputField, CheckBoxField, ParticipantSelectFieldWithMutate } from '../../Fields';
import { PersonalDataError } from '../PersonalDataError';
import { DefaultComponentProps } from '../types';
import { useRequest } from '../hooks';
import { PluginNotInstalled, PluginNotInstalledProps } from './PluginNonInstalled';
import { Certificate, CertInfo, FormValues } from './types';
import styles from './index.module.scss';


export const clientRole = '9312b1cb-3d0e-442d-9e4e-ac737eec6448';

const phoneMask = '+7(999)999-99-99';

const ogrnValidator = (ogrn: string, values: object) => validators.pipe(
    validators.required,
    fp.get('isIP', values) ? getError({ key: 'ogrnip' }, checkOgrnip)
        : getError({ key: 'ogrn' }, checkOgrn),
)(ogrn);

const validationNone = getError({ key: 'none' }, v => fp.isEmpty(v));

const companyValidation = (value: DaDataSuggestion<DaDataParty>, values: FormValues) =>
    values.certInn && fp.pipe(
        fp.get('data.inn'),
        fp.negate(fp.isEqual(values.certInn)),
    )(value) ? { key: 'differenceINN' } : undefined;

const getFromInfo = fp.curry<CertInfo['title'], CertInfo[], string>((title, info) => fp.pipe(
    fp.find(fp.pipe(
        fp.get('title'),
        fp.equals(title),
    )),
    fp.get('descr'),
    fp.trim,
)(info));

type Getter = (info: CertInfo[]) => string | boolean | object;

const getInnFromInfo = fp.pipe(
    (info: CertInfo[]) => getFromInfo('ИНН ЮЛ', info) || getFromInfo('ИНН', info),
    inn => inn.startsWith('00') ? inn.substring(2) : inn,
);

const getOgrnFromInfo = info => getFromInfo('ОГРНИП', info) || getFromInfo('ОГРН', info);

const isIPCert: Getter = info => !!getFromInfo('ОГРНИП', info);
const isIndividual: Getter = info => !isIPCert(info) && !getFromInfo('ОГРН', info);

const mapCertInfo: Common.Catalog<Getter> = {
    surname: info => getFromInfo('Фамилия', info) || getFromInfo('Владелец', info),
    firstName: fp.pipe(
        getFromInfo('Имя Отчество'),
        fp.split(' '),
        fp.get(0),
    ),
    middleName: fp.pipe(
        getFromInfo('Имя Отчество'),
        fp.split(' '),
        fp.drop(1),
        fp.join(' '),
    ),
    certInn: info => !isIndividual(info) ? getInnFromInfo(info) : null,
    isIP: isIPCert,
    isIndividual,
    kpp: getFromInfo('KPP'),
    ogrn: getOgrnFromInfo,
    snils: fp.pipe(
        getFromInfo('СНИЛС'),
        snils => {
            if (!snils)
                return '';

            if (checkSnils(snils))
                return snils;

            const formattedSnils = `${snils.substring(0, 3)}-${snils.substring(3, 6)}-${snils.substring(6, 9)} ${snils.substring(9, 12)}`;

            if (checkSnils(formattedSnils))
                return formattedSnils;

            return '';
        },
    ),
    company: info => ({
        data: {
            ...isIndividual(info) ? {} : {
                inn: info && getInnFromInfo(info),
                ogrn: getOgrnFromInfo,
                name: {
                    short_with_opf: getFromInfo('Компания', info),
                },
            },
        },
    }),
};

const formMutators: FinalFormProps<FormValues>['mutators'] = {
    setCertificate: ([{ info }], state, { changeValue }) => fp.pipe(
        fp.mapValues<Getter, ReturnType<Getter>>(getter => getter(info)),
        fp.toPairs,
        fp.forEach(([key, value]) => changeValue(state, key, () => value)),
    )(mapCertInfo),
    onCompanyChange: ([data], state, { changeValue }) => {
        changeValue(state, 'isIP', () => checkOgrnip(fp.get('data.ogrn', data)));
        changeValue(state, 'ogrn', () => fp.get('data.ogrn', data));
        changeValue(state, 'kpp', () => '');
    },
};

const formDataMap = {
    Surname: fp.get('surname'), // Ф
    FirstName: fp.get('firstName'), // И
    MiddleName: fp.get('middleName'), // О
    Email: fp.get('email'),
    PhoneNumber: fp.get('phone'),
    CompanyName: fp.get('company.data.name.short_with_opf'), // dadata field
    CompanyINN: fp.get('company.data.inn'), // dadata field
    CompanyKPP: fp.get('kpp'),
    SNILS: fp.get('snils'),
    CompanyOGRN: fp.get('ogrn'),
    AllowUsePersonalData: fp.get('usePersonalData'),
    Base64Certificate: fp.get('certificate.base64'),
};

type FormProps = DefaultComponentProps & {
    errorDetails?: PluginNotInstalledProps['details'];
    certificates?: Certificate[];
}

export const Form: FC<FormProps> = ({
    t,
    setStage,
    certificates = [],
    landingOwnerUid,
    errorDetails,
}) => {
    const onSuccess = useCallback(
        (values, data) => data.Created
            ? setStage('success', { data, values })
            : setStage('resendLetter', {
                email: values.email,
                timer: data.ConfirmCodeRequestRemaining,
            }),
        [setStage],
    );
    const onReject = useCallback(
        error => setStage(
            'failure',
            { error: fp.get('response.data.Title', error) || t('failure.unknown') },
        ),
        [setStage, t],
    );
    const mapFormValuesToApi = useCallback(
        (values: FormValues) => ({
            ...fp.mapValues(getter => getter(values), formDataMap),
            UserRoleUid: clientRole,
            LandingOwnerUid: landingOwnerUid,
        }),
        [landingOwnerUid],
    );
    const { busy, request } = useRequest<FormValues>(
        '/registration/create',
        mapFormValuesToApi,
        'POST',
        onSuccess,
        onReject,
    );

    const certificateOptions = useMemo(
        () => certificates.map(cert => ({
            label: cert.label,
            value: cert,
        })),
        [certificates],
    );

    const initialValues = useMemo(
        () => errorDetails ? {} : {
            certificate: certificates[0],
            ...fp.mapValues(getter => getter(certificates[0].info), mapCertInfo),
        },
        [errorDetails, certificates],
    );

    const disabledFields = busy || !!errorDetails;

    return (
        <div>
            <Label
                className={styles.header}
                weight='demibold'
                textSize='32'
                component='div'
            >
                {t('form.header')}
            </Label>
            {errorDetails && (
                <PluginNotInstalled
                    details={errorDetails}
                    setStage={setStage}
                    t={t}
                />
            )}
            {!errorDetails && (
                <Text className={styles.note} component='div' textSize='18'>
                    {t('form.description')}
                </Text>
            )}
            <FinalForm<FormValues>
                onSubmit={request}
                mutators={formMutators}
                initialValues={initialValues}
                subscription={{ hasValidationErrors: true, values: true, errors: true }}
            >
                {({
                    form: {
                        mutators: {
                            setCertificate,
                            onCompanyChange,
                        },
                    },
                    values,
                    hasValidationErrors,
                    handleSubmit,
                }) => (
                    <form className={styles.form} onSubmit={handleSubmit}>
                        <GridRow>
                            <GridColumn size={4}>
                                <Field
                                    name='certificate'
                                    component={CertificateField}
                                    size='56'
                                    validate={validators.required}
                                    label={t('entry.fields.certificate.label')}
                                    placeholder={t('entry.fields.certificate.placeholder')}
                                    needInitialValidate={!errorDetails}

                                    mutate={setCertificate}
                                    disabled={disabledFields}
                                    options={certificateOptions}
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow>
                            <GridColumn size={4}>
                                <Field
                                    name='surname'
                                    component={InputField}
                                    size='56'
                                    validate={validators.required}
                                    label={t('entry.fields.surname.label')}
                                    placeholder={t('entry.fields.surname.placeholder')}
                                    needInitialValidate={!errorDetails}

                                    disabled={disabledFields}

                                    readOnly
                                />
                            </GridColumn>
                            <GridColumn size={4}>
                                <Field
                                    name='firstName'
                                    component={InputField}
                                    size='56'
                                    validate={validators.required}
                                    label={t('entry.fields.firstName.label')}
                                    placeholder={t('entry.fields.firstName.placeholder')}
                                    needInitialValidate={!errorDetails}

                                    disabled={disabledFields}

                                    readOnly
                                />
                            </GridColumn>
                            <GridColumn size={4}>
                                <Field
                                    name='middleName'
                                    component={InputField}
                                    size='56'
                                    label={t('entry.fields.middleName.label')}
                                    placeholder={t('entry.fields.middleName.placeholder')}
                                    needInitialValidate={!errorDetails}

                                    disabled={disabledFields}

                                    readOnly
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow>
                            <GridColumn size={4}>
                                <Field
                                    name='company'
                                    component={ParticipantSelectFieldWithMutate}
                                    mutate={onCompanyChange}
                                    size='56'
                                    validate={(value, values) =>
                                        validators.required(value) ||
                                            companyValidation(value, values as FormValues)}
                                    label={t('lead.fields.company.label')}
                                    needInitialValidate={!errorDetails}

                                    disabled={disabledFields}
                                    readOnly={!fp.get('isIndividual', values)}
                                />
                            </GridColumn>
                            <GridColumn size={4}>
                                <Field
                                    name='ogrn'
                                    component={InputField}
                                    size='56'
                                    validate={ogrnValidator}
                                    label={t(`entry.fields.ogrn.label.${fp.get('isIP', values)
                                        ? 'individual'
                                        : 'company'
                                    }`)}
                                    placeholder='1234567890123'
                                    needInitialValidate={!errorDetails}
                                    disabled={disabledFields}
                                    readOnly
                                />
                            </GridColumn>
                            <GridColumn size={4}>
                                <Field
                                    name='snils'
                                    component={InputField}
                                    size='56'
                                    validate={validators.pipe(
                                        validators.required,
                                        validators.is.snils,
                                    )}
                                    label={t('entry.fields.snils.label')}
                                    placeholder='123-456-789 01'
                                    needInitialValidate={!errorDetails}

                                    disabled={disabledFields}
                                    mask='999-999-999 99'

                                    readOnly
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow>
                            <GridColumn size={4}>
                                <Field
                                    name='kpp'
                                    component={InputField}
                                    size='56'
                                    validate={(kpp, values) =>
                                        fp.get('isIP', values)
                                            ? validationNone(kpp)
                                            : validators.pipe(
                                                validators.required,
                                                validators.is.kpp,
                                            )(kpp)
                                    }
                                    label={t('entry.fields.kpp.label')}
                                    placeholder={fp.get('isIP', values)
                                        ? t('entry.fields.notApplicable')
                                        : '123456789'}
                                    needInitialValidate={!errorDetails}

                                    disabled={disabledFields || fp.get('isIP', values)}
                                />
                            </GridColumn>
                            <GridColumn size={4}>
                                <Field
                                    name='phone'
                                    component={InputField}
                                    size='56'
                                    validate={validators.required}
                                    label={t('lead.fields.phone.label')}
                                    placeholder={t('lead.fields.phone.placeholder')}
                                    needInitialValidate={!errorDetails}

                                    disabled={disabledFields}
                                    mask={phoneMask}
                                />
                            </GridColumn>
                            <GridColumn size={4}>
                                <Field
                                    name='email'
                                    component={InputField}
                                    size='56'
                                    validate={validators.required}
                                    label={t('lead.fields.email.label')}
                                    placeholder={t('lead.fields.email.placeholder')}
                                    needInitialValidate={!errorDetails}

                                    disabled={disabledFields}
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow className={styles.personalData}>
                            <Field
                                name='usePersonalData'
                                component={CheckBoxField}
                                validate={validators.required}
                                disabled={disabledFields}
                                type='checkbox'
                            >
                                <Label
                                    component='span'
                                    textSize='14'
                                >
                                    {t('lead.fields.legal', { linkClass: linkAnchorClass })}
                                </Label>
                            </Field>
                            <FormSpy
                                subscription={{ errors: true }}
                                component={PersonalDataError}
                            />
                        </GridRow>
                        <Button
                            type='submit'
                            variant='normal'
                            size='56'
                            className={styles.button}
                            disabled={hasValidationErrors || disabledFields}
                        >
                            {t('form.button')}
                        </Button>
                    </form>
                )}
            </FinalForm>
        </div>
    );
};
